import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api'; // ✅ Nutze konfigurierten apiService mit Cookies
import { getApiBaseUrl } from '../utils/apiConfig';
import { todayDateOnlyLocal } from '../utils/dateOnly';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as PreviewIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  CallSplit as SplitIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Euro as EuroIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import SimpleKontoSelector from './SimpleKontoSelector';
import SimpleObjectSelector from './SimpleObjectSelector';

const BelegUploadProfessional = () => {
  // 🔄 State Management
  const navigate = useNavigate();
  const [belege, setBelege] = useState([]);
  const [buchungskonten, setBuchungskonten] = useState([]);
  const [objekte, setObjekte] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedBeleg, setSelectedBeleg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // 📊 Statistiken State
  const [statistics, setStatistics] = useState({
    totalBelege: 0,
    totalValue: 0,
    lastUpload: null
  });

  // 🎯 Snackbar Helper
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // 📊 Alle Daten laden
  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [belegeRes, kontoRes, kontenRes, objekteRes] = await Promise.all([
        api.get('/api/documents'),
        api.get('/api/kontobewegung'),
        api.get('/api/konten', { params: { konto_typ: 'SACHKONTO' } }), // ✅ NEUE konten API
        api.get('/api/objects') // ✅ KORRIGIERT: objects (Plural)
      ]);

      const belegeData = Array.isArray(belegeRes) ? belegeRes : [];
      const kontoData = Array.isArray(kontoRes) ? kontoRes : [];
      // ✅ API gibt { konten: [...] } zurück
      const kontenData = Array.isArray(kontenRes?.konten) ? kontenRes.konten : (Array.isArray(kontenRes) ? kontenRes : []);
      const objekteData = Array.isArray(objekteRes) ? objekteRes : [];

      console.log('📦 Konten geladen:', kontenData.length, kontenData);

      setBelege(belegeData);
      setBuchungskonten(kontenData); // Wird noch buchungskonten State genannt, aber enthält konten
      setObjekte(objekteData);

      // Debug: Kontobewegungen loggen
      console.log('🔍 Geladene Kontobewegungen:', kontoData);
      console.log('🏢 Geladene Objekte:', objekteData);
      if (kontoData.length > 0) {
        console.log('📊 Erste Kontobewegung Struktur:', kontoData[0]);
      }

      // Statistiken berechnen
      setStatistics({
        totalBelege: belegeData.length,
        totalValue: belegeData.reduce((sum, beleg) => sum + parseFloat(beleg.belegbetrag || 0), 0),
        lastUpload: belegeData.length > 0 ? new Date(Math.max(...belegeData.map(b => new Date(b.created_at || Date.now())))) : null
      });

    } catch (error) {
      console.error('❌ Fehler beim Laden der Daten:', error);
      showSnackbar('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // 📁 Drag & Drop Handling
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    // Backend akzeptiert ausschließlich PDF – Nicht-PDFs sofort abweisen (auch bei Drag & Drop)
    const rejected = files.filter(f => f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf'));
    if (rejected.length > 0) {
      showSnackbar(`Nur PDF-Dateien erlaubt. Abgewiesen: ${rejected.map(f => f.name).join(', ')}`, 'error');
    }
    const pdfFiles = files.filter(f => !rejected.includes(f));
    if (pdfFiles.length === 0) return;

    const fileObjects = pdfFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file),
      betrag: '',
      datum: todayDateOnlyLocal(),
      beschreibung: file.name.replace(/\.[^/.]+$/, ""),
      buchungskonto: '',
      objektid: '', // 🆕 NEU: Objekt für normale Buchung
      useSplit: false,
      splits: [
        { 
          betrag: '', 
          buchungskonto: '', 
          objektid: '', // 🆕 NEU: Objekt für Split-Buchung
          beschreibung: '',
          umsatzsteuersatz: 19,
          nettoanteil: 0,
          steueranteil: 0
        }
      ]
    }));
    
    setSelectedFiles(prev => [...prev, ...fileObjects]);
    setUploadDialogOpen(true);
  };

  // 💰 Split-Buchung Funktionen
  const toggleSplit = (fileIndex) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[fileIndex].useSplit = !updatedFiles[fileIndex].useSplit;
    
    if (updatedFiles[fileIndex].useSplit && updatedFiles[fileIndex].betrag) {
      updatedFiles[fileIndex].splits = [
        {
          betrag: updatedFiles[fileIndex].betrag,
          buchungskonto: '',
          objektid: '', // 🆕 NEU: Objekt für Split-Buchung
          beschreibung: 'Hauptbuchung',
          umsatzsteuersatz: 19,
          nettoanteil: calculateNettoAnteil(updatedFiles[fileIndex].betrag, 19),
          steueranteil: calculateSteuerAnteil(updatedFiles[fileIndex].betrag, 19)
        }
      ];
    }
    
    setSelectedFiles(updatedFiles);
    validateSplits(fileIndex);
  };

  const addSplit = (fileIndex) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[fileIndex].splits.push({
      betrag: '',
      buchungskonto: '',
      objektid: '', // 🆕 NEU: Objekt für neue Split-Buchung
      beschreibung: '',
      umsatzsteuersatz: 19,
      nettoanteil: 0,
      steueranteil: 0
    });
    setSelectedFiles(updatedFiles);
  };

  const removeSplit = (fileIndex, splitIndex) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[fileIndex].splits.splice(splitIndex, 1);
    setSelectedFiles(updatedFiles);
    validateSplits(fileIndex);
  };

  const updateSplit = (fileIndex, splitIndex, field, value) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[fileIndex].splits[splitIndex][field] = value;
    
    // Steuerberechnung bei Betrag oder Steuersatz Änderung
    if (field === 'betrag' || field === 'umsatzsteuersatz') {
      const betrag = parseFloat(value) || 0;
      const steuersatz = field === 'umsatzsteuersatz' ? parseFloat(value) : updatedFiles[fileIndex].splits[splitIndex].umsatzsteuersatz;
      
      if (field === 'betrag') {
        updatedFiles[fileIndex].splits[splitIndex].nettoanteil = calculateNettoAnteil(betrag, steuersatz);
        updatedFiles[fileIndex].splits[splitIndex].steueranteil = calculateSteuerAnteil(betrag, steuersatz);
      }
    }
    
    setSelectedFiles(updatedFiles);
    validateSplits(fileIndex);
  };

  // 🧮 Steuerberechnungen
  const calculateNettoAnteil = (bruttoBetrag, steuersatz) => {
    if (!bruttoBetrag || !steuersatz) return 0;
    return bruttoBetrag / (1 + steuersatz / 100);
  };

  const calculateSteuerAnteil = (bruttoBetrag, steuersatz) => {
    if (!bruttoBetrag || !steuersatz) return 0;
    const netto = calculateNettoAnteil(bruttoBetrag, steuersatz);
    return bruttoBetrag - netto;
  };

  // ✅ Validierung
  const validateSplits = (fileIndex) => {
    const file = selectedFiles[fileIndex];
    if (!file.useSplit) {
      setValidationError('');
      return true;
    }

    const originalBetrag = parseFloat(file.betrag || 0);
    const splitSumme = file.splits.reduce((sum, split) => sum + parseFloat(split.betrag || 0), 0);
    const differenz = Math.abs(originalBetrag - splitSumme);

    if (differenz > 0.01) {
      setValidationError(`Split-Summe (${splitSumme.toFixed(2)}€) stimmt nicht mit Gesamtbetrag (${originalBetrag.toFixed(2)}€) überein!`);
      return false;
    }

    setValidationError('');
    return true;
  };

  const updateFileData = (index, field, value) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[index][field] = value;
    
    // Bei Betrag-Änderung: Splits automatisch anpassen
    if (field === 'betrag' && updatedFiles[index].useSplit && value) {
      updatedFiles[index].splits = [
        {
          ...updatedFiles[index].splits[0],
          betrag: value,
          nettoanteil: calculateNettoAnteil(parseFloat(value), 19),
          steueranteil: calculateSteuerAnteil(parseFloat(value), 19)
        }
      ];
    }
    
    setSelectedFiles(updatedFiles);
    
    if (updatedFiles[index].useSplit) {
      validateSplits(index);
    }
  };

  const removeFile = (index) => {
    const updatedFiles = [...selectedFiles];
    URL.revokeObjectURL(updatedFiles[index].preview);
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  };

  // 📤 Upload-Funktionen (verbesserte Cloudinary-Integration)
  const handleUpload = async () => {
    try {
      setLoading(true);
      setUploadProgress(0);
      let successCount = 0;

      for (let i = 0; i < selectedFiles.length; i++) {
        const fileData = selectedFiles[i];
        
        // Validierung vor Upload
        if (!fileData.betrag || !fileData.datum) {
          showSnackbar(`Datei ${fileData.name}: Betrag und Datum sind erforderlich`, 'error');
          continue;
        }

        if (fileData.useSplit && !validateSplits(i)) {
          showSnackbar(`Datei ${fileData.name}: Split-Validierung fehlgeschlagen`, 'error');
          continue;
        }

        const formData = new FormData();
        formData.append('file', fileData.file);
        formData.append('belegDatum', fileData.datum);
        formData.append('belegBetrag', fileData.betrag);
        formData.append('beschreibung', fileData.beschreibung);

        try {
          console.log(`📤 Lade Datei ${i + 1}/${selectedFiles.length} hoch: ${fileData.name}`);
          console.log('📦 FormData enthält:', {
            file: fileData.file.name,
            size: fileData.file.size,
            type: fileData.file.type,
            belegDatum: fileData.datum,
            belegBetrag: fileData.betrag
          });

          // Verwende die verbesserte Cloudinary-Route
          // WICHTIG: Content-Type NICHT manuell setzen - Axios muss boundary generieren
          const response = await api.post('/api/documents/upload', formData, {
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                ((i * 100) + (progressEvent.loaded * 100) / progressEvent.total) / selectedFiles.length
              );
              setUploadProgress(percentCompleted);
            },
            timeout: 30000 // 30 Sekunden Timeout für große Dateien
          });

          console.log(`✅ Upload erfolgreich:`, response);

          // Split-Buchungen erstellen falls aktiviert
          if (fileData.useSplit && response.belegid) {
            await createSplitBuchungen(response.belegid, fileData.splits);
          } else if (!fileData.useSplit && fileData.buchungskonto) {
            // Normale Buchung nur erstellen wenn Buchungskonto ausgewählt wurde
            // Ansonsten wird die Buchung später bei der Zuordnung erstellt
            await createBuchung({
              buchungskontoid: fileData.buchungskonto,
              buchungsbetrag: fileData.betrag,
              notiz: fileData.beschreibung,
              buchungsdatum: fileData.datum,
              belegid: response.belegid || null,
              objektid: fileData.objektid || null // 🆕 NEU: Objekt übertragen
            });
          }

          successCount++;
          URL.revokeObjectURL(fileData.preview);

        } catch (fileError) {
          console.error(`❌ Upload Fehler für ${fileData.name}:`, fileError);
          console.error('❌ Error Object:', {
            message: fileError.message,
            response: fileError.response,
            responseData: fileError.response?.data,
            responseStatus: fileError.response?.status,
            responseHeaders: fileError.response?.headers,
            config: fileError.config
          });
          
          // Detaillierte Fehlermeldung basierend auf HTTP-Status
          let errorMessage = fileError.response?.data?.error || fileError.message;
          
          // Debug-Infos in Fehlermeldung einbauen
          if (fileError.response?.data?.debug) {
            const debug = fileError.response.data.debug;
            errorMessage += ` (Content-Type: ${debug.contentType}, Body Keys: ${debug.bodyKeys?.join(', ') || 'keine'})`;
          }
          
          // 409 Conflict = Dokument existiert bereits
          if (fileError.response?.status === 409) {
            errorMessage = `Das Dokument "${fileData.name}" wurde bereits hochgeladen und existiert in der Datenbank.`;
          }
          
          showSnackbar(`Upload-Fehler: ${errorMessage}`, 'error');
        }
      }

      if (successCount > 0) {
        showSnackbar(`${successCount} Beleg(e) erfolgreich hochgeladen und in Cloudinary gespeichert`, 'success');
        setUploadDialogOpen(false);
        setSelectedFiles([]);
        await loadAllData(); // Daten neu laden
      } else {
        showSnackbar('Keine Dateien konnten hochgeladen werden', 'warning');
      }

    } catch (error) {
      console.error('❌ Upload Batch-Fehler:', error);
      showSnackbar('Fehler beim Upload-Prozess', 'error');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // 📝 Buchungen erstellen
  const createBuchung = async (buchungData) => {
    try {
      await api.post('/api/buchung', buchungData);
    } catch (error) {
      console.error('Fehler beim Erstellen der Buchung:', error);
      throw error;
    }
  };

  const createSplitBuchungen = async (belegid, splits) => {
    try {
      for (const split of splits) {
        if (split.betrag && split.buchungskonto) {
          await createBuchung({
            buchungskontoid: split.buchungskonto,
            buchungsbetrag: split.betrag,
            notiz: split.beschreibung,
            belegid: belegid || null,
            objektid: split.objektid || null, // 🆕 NEU: Objekt für Split-Buchung übertragen
            umsatzsteuersatz: split.umsatzsteuersatz || null // ✅ USt-Satz übertragen
          });
        }
      }
    } catch (error) {
      console.error('Fehler beim Erstellen der Split-Buchungen:', error);
      throw error;
    }
  };

  // 👁️ Vorschau-Funktionen
  const openPreview = (beleg) => {
    setSelectedBeleg(beleg);
    setPreviewDialogOpen(true);
  };

  // 🖼️ Cloudinary PDF-zu-Bild URL Konvertierung
  const getCloudinaryImageUrl = (cloudinaryUrl) => {
    if (!cloudinaryUrl || !cloudinaryUrl.includes('cloudinary.com')) {
      return cloudinaryUrl;
    }
    
    // Prüfe ob es bereits eine Bild-URL ist
    if (cloudinaryUrl.includes('/image/upload/')) {
      console.log('🖼️ URL ist bereits Bild-Format:', cloudinaryUrl);
      return cloudinaryUrl;
    }
    
    // Konvertiere raw PDF-URL zu Bild-URL
    if (cloudinaryUrl.includes('/raw/upload/')) {
      const imageUrl = cloudinaryUrl
        .replace('/raw/upload/', '/image/upload/')
        .replace(/\.pdf$/, '.jpg');
      console.log('🔄 Konvertiere PDF zu Bild-URL:', cloudinaryUrl, '->', imageUrl);
      return imageUrl;
    }
    
    // Fallback: Versuche URL-Struktur zu erkennen und anzupassen
    if (cloudinaryUrl.includes('/upload/') && cloudinaryUrl.endsWith('.pdf')) {
      const imageUrl = cloudinaryUrl
        .replace('/upload/', '/image/upload/')
        .replace(/\.pdf$/, '.jpg');
      console.log('🔄 Fallback Konvertierung zu Bild-URL:', cloudinaryUrl, '->', imageUrl);
      return imageUrl;
    }
    
    return cloudinaryUrl;
  };

  const getBelegUrl = (beleg) => {
    // Priorisierung: Cloudinary URL hat Vorrang
    if (beleg.cloudinary_url && beleg.cloudinary_url.startsWith('http')) {
      console.log('📄 Verwende Cloudinary URL für:', beleg.belegname || beleg.BelegName);
      const imageUrl = getCloudinaryImageUrl(beleg.cloudinary_url);
      return imageUrl;
    }
    
    // Fallback für legacy file_url
    if (beleg.file_url && beleg.file_url.startsWith('http')) {
      console.log('📄 Verwende file_url für:', beleg.belegname || beleg.BelegName);
      const imageUrl = getCloudinaryImageUrl(beleg.file_url);
      return imageUrl;
    }
    
    // Fallback für lokale Dateien (legacy)
    if (beleg.belegpfad && beleg.belegpfad.startsWith('http')) {
      console.log('📄 Verwende belegpfad URL für:', beleg.belegname || beleg.BelegName);
      return beleg.belegpfad;
    }
    
    // Legacy lokaler Pfad (sollte nicht mehr verwendet werden)
    if (beleg.belegpfad) {
      console.warn('⚠️ Verwende legacy lokalen Pfad für:', beleg.belegname || beleg.BelegName);
      const API_BASE_URL = getApiBaseUrl();
      let cleanPath = beleg.belegpfad;
      
      if (cleanPath.startsWith('/uploads/')) {
        cleanPath = cleanPath.substring(9);
      } else if (cleanPath.startsWith('uploads/')) {
        cleanPath = cleanPath.substring(8);
      } else if (cleanPath.startsWith('/')) {
        cleanPath = cleanPath.substring(1);
      }
      
      return `${API_BASE_URL}/uploads/${cleanPath}`;
    }
    
    console.error('❌ Keine gültige URL gefunden für Beleg:', beleg);
    return null;
  };

  const getFileType = (filename, belegUrl = null) => {
    // Prüfe zuerst Cloudinary-URL für konvertierte PDFs
    if (belegUrl && belegUrl.includes('cloudinary.com')) {
      if (belegUrl.includes('/image/upload/') || belegUrl.endsWith('.jpg') || belegUrl.endsWith('.jpeg')) {
        console.log('🖼️ Erkenne Cloudinary-Bild als image-Typ:', belegUrl);
        return 'image';
      }
    }
    
    // Fallback auf Dateiendung
    const ext = filename.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    return 'document';
  };

  // 🎨 Format-Funktionen
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 🏢 Gruppierung der Splits nach Objekten
  const groupSplitsByObject = (splits) => {
    const groups = {};
    
    splits.forEach((split, index) => {
      const objektId = split.objektid || 'ohne_objekt';
      if (!groups[objektId]) {
        groups[objektId] = {
          objekt: split.objektid ? objekte.find(o => o.objektid === split.objektid) : null,
          splits: []
        };
      }
      groups[objektId].splits.push({ ...split, originalIndex: index });
    });
    
    return groups;
  };

  return (
    <Box>
      {/* Header mit modernem Gradient und Zurück-Button */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
        borderRadius: 2,
        p: 3,
        mb: 3,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            📄 Professionelle Beleg-Verwaltung
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Upload, Split-Buchung, Vorschau und vollständige Dokumentenverwaltung
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            sx={{
              background: 'linear-gradient(90deg, #d69e2e 0%, #b7791f 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              ml: 2,
              mt: { xs: 2, md: 0 },
              '&:hover': {
                background: 'linear-gradient(90deg, #b7791f 0%, #d69e2e 100%)',
                color: '#fff',
              },
            }}
            onClick={() => navigate('/dashboard')}
          >
            Zurück zum Dashboard
          </Button>
        </Box>
      </Box>

      {/* Statistiken-Dashboard */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ fontSize: 40, color: '#1a365d', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a365d' }}>
                    {statistics.totalBelege}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gespeicherte Belege
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EuroIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {formatCurrency(statistics.totalValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gesamtwert
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#16a34a', mr: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#16a34a' }}>
                    {statistics.lastUpload ? formatDate(statistics.lastUpload) : 'Kein Upload'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Letzter Upload
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Drag & Drop Upload Area */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                border: dragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: dragOver ? '#f3f8ff' : '#fafafa',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f3f8ff'
                }
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: dragOver ? '#1976d2' : '#666', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: dragOver ? '#1976d2' : 'inherit' }}>
                {dragOver ? 'Dateien hier ablegen!' : 'Dateien hier ablegen oder klicken'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Nur PDF-Dateien | Max. 10MB pro Datei
              </Typography>
              <input
                id="file-input"
                type="file"
                multiple
                accept=".pdf,application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1a365d' }}>
                🚀 Features
              </Typography>
              <Stack spacing={1}>
                <Chip label="✂️ Split-Buchung" color="primary" variant="outlined" />
                <Chip label="🏢 Objekt-Zuordnung" color="info" variant="outlined" />
                <Chip label="🧮 Steuerberechnung" color="secondary" variant="outlined" />
                <Chip label="👁️ PDF-Vorschau" color="success" variant="outlined" />
                <Chip label="📊 Upload-Progress" color="info" variant="outlined" />
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Belege Übersicht */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#1a365d' }} />
            Gespeicherte Belege ({belege.length})
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>Dateiname</strong></TableCell>
                  <TableCell><strong>Datum</strong></TableCell>
                  <TableCell><strong>Betrag</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {belege.map((beleg) => (
                  <TableRow key={beleg.belegid} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon sx={{ color: '#1a365d' }} />
                        {beleg.belegname || beleg.BelegName}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(beleg.belegdatum)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {formatCurrency(beleg.belegbetrag)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label="Gespeichert" 
                        color="success" 
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Vorschau">
                        <IconButton
                          onClick={() => openPreview(beleg)}
                          sx={{ color: '#1a365d' }}
                        >
                          <PreviewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: '85vh',
            maxHeight: '95vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          📤 Professionelle Beleg-Verarbeitung
        </DialogTitle>
        <DialogContent sx={{ mt: 2, maxHeight: '70vh', overflow: 'auto' }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}
          
          {uploadProgress > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Upload-Fortschritt: {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
          
          {selectedFiles.map((fileData, fileIndex) => (
            <Accordion key={fileIndex} defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <ReceiptIcon sx={{ color: '#1a365d' }} />
                  <Typography variant="h6">{fileData.name}</Typography>
                  {fileData.useSplit && (
                    <Chip label="Split-Buchung" color="primary" size="small" />
                  )}
                  <Box sx={{ ml: 'auto' }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(fileData.size)}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  
                  {/* Vorschau */}
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1a365d' }}>
                      📄 Dokument-Vorschau
                    </Typography>
                    {fileData.type === 'application/pdf' ? (
                      <Box sx={{
                        height: 400,
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}>
                        <iframe
                          src={fileData.preview}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                          title={`PDF Vorschau: ${fileData.name}`}
                        />
                      </Box>
                    ) : fileData.preview ? (
                      <Box sx={{
                        height: 400,
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <img
                          src={fileData.preview}
                          alt="Vorschau"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{
                        height: 400,
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f9f9f9'
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          📄 Keine Vorschau verfügbar
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {/* Beleg-Daten */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1a365d' }}>
                      💰 Beleg-Daten
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label="Gesamtbetrag"
                        type="number"
                        fullWidth
                        size="small"
                        value={fileData.betrag}
                        onChange={(e) => updateFileData(fileIndex, 'betrag', e.target.value)}
                        InputProps={{
                          startAdornment: <EuroIcon sx={{ mr: 1, color: '#1a365d' }} />
                        }}
                        required
                      />
                      <TextField
                        label="Datum"
                        type="date"
                        fullWidth
                        size="small"
                        value={fileData.datum}
                        onChange={(e) => updateFileData(fileIndex, 'datum', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                      <TextField
                        label="Beschreibung"
                        fullWidth
                        size="small"
                        value={fileData.beschreibung}
                        onChange={(e) => updateFileData(fileIndex, 'beschreibung', e.target.value)}
                        multiline
                        rows={3}
                      />
                    </Stack>
                  </Grid>

                  {/* Buchungs-Zuordnung */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a365d' }}>
                        🔗 Buchungs-Zuordnung
                      </Typography>
                      <Button
                        variant={fileData.useSplit ? "contained" : "outlined"}
                        startIcon={<SplitIcon />}
                        onClick={() => toggleSplit(fileIndex)}
                        size="small"
                      >
                        Split-Buchung
                      </Button>
                    </Box>

                    {fileData.useSplit ? (
                      <Card sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: '#1a365d' }}>
                          Split-Buchungen {fileData.splits.length > 1 && '(gruppiert nach Objekt)'}
                        </Typography>
                        
                        {(() => {
                          const splitGroups = groupSplitsByObject(fileData.splits);
                          
                          return Object.entries(splitGroups).map(([objektId, group]) => (
                            <Box key={objektId} sx={{ mb: 3 }}>
                              {/* Objekt-Gruppierung Header */}
                              {Object.keys(splitGroups).length > 1 && (
                                <Box sx={{ 
                                  mb: 2, 
                                  p: 2, 
                                  backgroundColor: objektId === 'ohne_objekt' ? '#fff3e0' : '#e3f2fd',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2
                                }}>
                                  {objektId === 'ohne_objekt' ? (
                                    <>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        🏷️ Ohne Objekt-Zuordnung
                                      </Typography>
                                      <Chip 
                                        label={`${group.splits.length} Buchung(en)`} 
                                        size="small" 
                                        color="warning" 
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        🏢 {group.objekt?.objektname || 'Unbekanntes Objekt'}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {group.objekt?.objektadresse}
                                      </Typography>
                                      <Chip 
                                        label={group.objekt?.objekttyp || 'Unbekannt'} 
                                        size="small" 
                                        color="info" 
                                      />
                                      <Chip 
                                        label={`${group.splits.length} Buchung(en)`} 
                                        size="small" 
                                        color="primary" 
                                      />
                                    </>
                                  )}
                                </Box>
                              )}
                              
                              {/* Splits in dieser Gruppe */}
                              {group.splits.map((split) => (
                                <Card key={split.originalIndex} sx={{ p: 2, mb: 2, backgroundColor: 'white' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Teilbuchung {split.originalIndex + 1}
                                      {group.objekt && (
                                        <Chip 
                                          label={group.objekt.objektname} 
                                          size="small" 
                                          sx={{ ml: 1 }}
                                          color="info"
                                        />
                                      )}
                                    </Typography>
                                    {fileData.splits.length > 1 && (
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => removeSplit(fileIndex, split.originalIndex)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                  </Box>
                                  
                                  <Stack spacing={2}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={4}>
                                        <TextField
                                          label="Betrag"
                                          type="number"
                                          fullWidth
                                          size="small"
                                          value={split.betrag}
                                          onChange={(e) => updateSplit(fileIndex, split.originalIndex, 'betrag', e.target.value)}
                                          InputProps={{ startAdornment: '€' }}
                                        />
                                      </Grid>
                                      <Grid item xs={4}>
                                        <TextField
                                          label="USt-Satz (%)"
                                          type="number"
                                          fullWidth
                                          size="small"
                                          value={split.umsatzsteuersatz}
                                          onChange={(e) => updateSplit(fileIndex, split.originalIndex, 'umsatzsteuersatz', e.target.value)}
                                        />
                                      </Grid>
                                      <Grid item xs={4}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                          <Tooltip title={`Netto: ${formatCurrency(split.nettoanteil)}`}>
                                            <Chip label={`N: ${split.nettoanteil.toFixed(2)}€`} size="small" />
                                          </Tooltip>
                                          <Tooltip title={`Steuer: ${formatCurrency(split.steueranteil)}`}>
                                            <Chip label={`S: ${split.steueranteil.toFixed(2)}€`} size="small" color="secondary" />
                                          </Tooltip>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                    
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                        Buchungskonto für Teilbuchung {split.originalIndex + 1}
                                      </Typography>
                                      <SimpleKontoSelector
                                        buchungskonten={buchungskonten}
                                        selectedKonto={buchungskonten.find(k => k.konto_id === split.buchungskonto)}
                                        onKontoSelect={(konto) => updateSplit(fileIndex, split.originalIndex, 'buchungskonto', konto.konto_id)}
                                        disabled={loading}
                                      />
                                    </Box>
                                    

                                    
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                        Objekt für Teilbuchung {split.originalIndex + 1} (optional)
                                      </Typography>
                                      <SimpleObjectSelector
                                        objekte={objekte}
                                        selectedObjekt={objekte.find(o => o.objektid === split.objektid)}
                                        onObjektSelect={(objekt) => updateSplit(fileIndex, split.originalIndex, 'objektid', objekt?.objektid)}
                                        disabled={loading}
                                      />
                                    </Box>
                                    
                                    <TextField
                                      label="Beschreibung"
                                      fullWidth
                                      size="small"
                                      value={split.beschreibung}
                                      onChange={(e) => updateSplit(fileIndex, split.originalIndex, 'beschreibung', e.target.value)}
                                    />
                                  </Stack>
                                </Card>
                              ))}
                            </Box>
                          ));
                        })()}
                        
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => addSplit(fileIndex)}
                          variant="outlined"
                          size="small"
                          fullWidth
                        >
                          Weitere Buchung hinzufügen
                        </Button>
                        
                        {/* Split-Summen-Validierung */}
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                          <Typography variant="body2">
                            <strong>Gesamtbetrag:</strong> {formatCurrency(fileData.betrag)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Split-Summe:</strong> {formatCurrency(
                              fileData.splits.reduce((sum, split) => sum + parseFloat(split.betrag || 0), 0)
                            )}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color={
                              Math.abs(
                                fileData.splits.reduce((sum, split) => sum + parseFloat(split.betrag || 0), 0) - 
                                parseFloat(fileData.betrag || 0)
                              ) > 0.01 ? 'error' : 'success'
                            }
                          >
                            <strong>Differenz:</strong> {formatCurrency(
                              fileData.splits.reduce((sum, split) => sum + parseFloat(split.betrag || 0), 0) - 
                              parseFloat(fileData.betrag || 0)
                            )}
                          </Typography>
                        </Box>
                      </Card>
                    ) : (
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a365d' }}>
                            Buchungskonto auswählen
                          </Typography>
                          <SimpleKontoSelector
                            buchungskonten={buchungskonten}
                            selectedKonto={buchungskonten.find(k => k.konto_id === fileData.buchungskonto)}
                            onKontoSelect={(konto) => updateFileData(fileIndex, 'buchungskonto', konto.konto_id)}
                            disabled={loading}
                          />
                        </Box>
                        

                        
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a365d' }}>
                            Objekt auswählen (optional)
                          </Typography>
                          <SimpleObjectSelector
                            objekte={objekte}
                            selectedObjekt={objekte.find(o => o.objektid === fileData.objektid)}
                            onObjektSelect={(objekt) => updateFileData(fileIndex, 'objektid', objekt?.objektid)}
                            disabled={loading}
                          />
                        </Box>
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                {/* Datei entfernen */}
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={() => removeFile(fileIndex)}
                    color="error"
                    variant="outlined"
                  >
                    Datei entfernen
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleUpload}
            variant="contained"
            disabled={loading || selectedFiles.length === 0 || validationError}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #153251 0%, #26507a 100%)'
              }
            }}
          >
            {loading ? 'Wird verarbeitet...' : 'Belege verarbeiten'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: '90vh',
            maxHeight: '95vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          📄 Beleg-Vorschau
        </DialogTitle>
        <DialogContent sx={{ mt: 2, maxHeight: '80vh', overflow: 'auto' }}>
          {selectedBeleg && (
            <Grid container spacing={3}>
              {/* Dokument-Info */}
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, height: 'fit-content' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1a365d' }}>
                    📋 Dokument-Informationen
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Dateiname
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedBeleg.belegname || selectedBeleg.BelegName}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Datum
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedBeleg.belegdatum)}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Betrag
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(selectedBeleg.belegbetrag)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Stack spacing={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => window.open(getBelegUrl(selectedBeleg), '_blank')}
                        sx={{ borderRadius: 2 }}
                      >
                        In neuem Tab öffnen
                      </Button>
                      
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = getBelegUrl(selectedBeleg);
                          link.download = selectedBeleg.belegname || selectedBeleg.BelegName;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        sx={{ borderRadius: 2 }}
                      >
                        Herunterladen
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              {/* Dokument-Vorschau */}
              <Grid item xs={12} md={9}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1a365d' }}>
                    📄 Dokument-Vorschau (Vollbildmodus)
                  </Typography>
                  <Box sx={{
                    height: 700,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    {(() => {
                      const belegUrl = getBelegUrl(selectedBeleg);
                      const fileType = getFileType(selectedBeleg.belegname || selectedBeleg.BelegName, belegUrl);
                      
                      console.log('🔍 Vorschau-Debug:', {
                        filename: selectedBeleg.belegname || selectedBeleg.BelegName,
                        detectedType: fileType,
                        url: belegUrl
                      });
                      
                      if (fileType === 'image' || belegUrl?.includes('/image/upload/')) {
                        return (
                          <img
                            src={belegUrl}
                            alt="Beleg Vorschau"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              backgroundColor: '#ffffff'
                            }}
                            onLoad={() => console.log('✅ Bild erfolgreich geladen:', belegUrl)}
                            onError={(e) => {
                              console.error('❌ Bild-Ladefehler:', belegUrl, e);
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        );
                      } else if (fileType === 'pdf') {
                        return (
                          <iframe
                            src={belegUrl}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none'
                            }}
                            title="PDF Vorschau"
                          />
                        );
                      } else {
                        return (
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            backgroundColor: '#f5f5f5',
                            flexDirection: 'column',
                            gap: 2
                          }}>
                            <ReceiptIcon sx={{ fontSize: 64, color: '#ccc' }} />
                            <Typography variant="h6" color="text.secondary">
                              📄 Vorschau nicht verfügbar
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Dateityp wird nicht unterstützt oder URL ist nicht verfügbar
                            </Typography>
                          </Box>
                        );
                      }
                    })()}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Nachrichten */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BelegUploadProfessional;
